import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { PileIndex } from 'features/battle/types';
import type { Badge } from 'features/catalog/useBadges';

export const REVEAL_DRAG_THRESHOLD = 8;
export const REVEAL_DRAG_MS = 220;
export const REVEAL_PILE_ATTR = 'data-bb-reveal-pile';

const PILES: readonly PileIndex[] = [0, 1, 2, 3];

export const pileAtPoint = (x: number, y: number): PileIndex | null => {
  if (typeof document === 'undefined' || typeof document.elementsFromPoint !== 'function') return null;
  for (const node of document.elementsFromPoint(x, y)) {
    const host = node instanceof Element ? node.closest(`[${REVEAL_PILE_ATTR}]`) : null;
    if (!(host instanceof HTMLElement)) continue;
    const pile = Number(host.getAttribute(REVEAL_PILE_ATTR));
    if (PILES.includes(pile as PileIndex)) return pile as PileIndex;
  }
  return null;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type DragPhase = 'idle' | 'press' | 'drag' | 'return';

export type RevealDragGhost = {
  cardId: string;
  badge: Badge;
  width: number;
  height: number;
  returning: boolean;
};

type Press = {
  cardId: string;
  badge: Badge;
  pointerId: number;
  startX: number;
  startY: number;
  origin: DOMRect;
  offsetX: number;
  offsetY: number;
};

type Lifted = { cardId: string; width: number; height: number };

export type RevealDragFns = {
  armed: boolean;
  legalPiles: (cardId: string) => readonly PileIndex[];
  onSelect: (cardId: string) => void;
  onHoverPile: (pile: PileIndex | null) => void;
  onDrop: (cardId: string, pile: PileIndex) => boolean;
  onCancel: () => void;
};

const measureReturnTarget = (slot: HTMLElement, width: number): DOMRect => {
  const prevTransition = slot.style.transition;
  const prevWidth = slot.style.width;
  const prevMargin = slot.style.marginInlineEnd;
  slot.style.transition = 'none';
  slot.style.width = `${width}px`;
  slot.style.marginInlineEnd = '';
  const rect = slot.getBoundingClientRect();
  slot.style.width = prevWidth;
  slot.style.marginInlineEnd = prevMargin;
  slot.style.transition = prevTransition;
  void slot.offsetWidth;
  return rect;
};

export const useRevealDrag = (fns: RevealDragFns) => {
  const fnsRef = useRef(fns);
  fnsRef.current = fns;

  const phaseRef = useRef<DragPhase>('idle');
  const pressRef = useRef<Press | null>(null);
  const liftedRef = useRef<Lifted | null>(null);
  const hoverRef = useRef<PileIndex | null>(null);
  const ghostElRef = useRef<HTMLDivElement | null>(null);
  const ghostPosRef = useRef({ x: 0, y: 0 });
  const slotRefs = useRef(new Map<string, HTMLDivElement>());
  const suppressClickRef = useRef<string | null>(null);
  const returnTimerRef = useRef(0);

  const [phase, setPhase] = useState<DragPhase>('idle');
  const [ghost, setGhost] = useState<RevealDragGhost | null>(null);
  const [lifted, setLifted] = useState<Lifted | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  phaseRef.current = phase;
  liftedRef.current = lifted;

  const moveGhost = (x: number, y: number, returning = false) => {
    ghostPosRef.current = { x, y };
    const el = ghostElRef.current;
    if (!el) return;
    el.style.transform = `translate(${x}px, ${y}px)`;
    if (returning) el.classList.add('is-returning');
    else el.classList.remove('is-returning');
  };

  const setHover = (pile: PileIndex | null) => {
    if (hoverRef.current === pile) return;
    hoverRef.current = pile;
    fnsRef.current.onHoverPile(pile);
  };

  const clearDrag = useCallback((cancelPick: boolean) => {
    window.clearTimeout(returnTimerRef.current);
    phaseRef.current = 'idle';
    pressRef.current = null;
    hoverRef.current = null;
    setPhase('idle');
    setGhost(null);
    setLifted(null);
    setCollapsed(false);
    if (cancelPick) fnsRef.current.onCancel();
    else fnsRef.current.onHoverPile(null);
  }, []);

  const startReturn = useCallback(() => {
    const press = pressRef.current;
    const liftedCard = liftedRef.current;
    if (!press || !liftedCard) {
      clearDrag(true);
      return;
    }
    if (prefersReducedMotion()) {
      clearDrag(true);
      return;
    }
    const slot = slotRefs.current.get(press.cardId);
    const dest = slot ? measureReturnTarget(slot, liftedCard.width) : press.origin;
    phaseRef.current = 'return';
    setPhase('return');
    setCollapsed(false);
    setGhost((current) => (current ? { ...current, returning: true } : current));
    const el = ghostElRef.current;
    if (el) {
      el.style.transition = 'none';
      el.style.transform = `translate(${ghostPosRef.current.x}px, ${ghostPosRef.current.y}px)`;
      void el.offsetWidth;
      el.classList.add('is-returning');
      el.style.transition = `transform ${REVEAL_DRAG_MS}ms cubic-bezier(0.22, 0.2, 0.2, 1)`;
      el.style.transform = `translate(${dest.left}px, ${dest.top}px)`;
    }
    window.clearTimeout(returnTimerRef.current);
    returnTimerRef.current = window.setTimeout(() => clearDrag(true), REVEAL_DRAG_MS + 40);
  }, [clearDrag]);

  const beginDrag = (event: PointerEvent, press: Press) => {
    suppressClickRef.current = press.cardId;
    phaseRef.current = 'drag';
    setPhase('drag');
    setCollapsed(false);
    setLifted({ cardId: press.cardId, width: press.origin.width, height: press.origin.height });
    const x = event.clientX - press.offsetX;
    const y = event.clientY - press.offsetY;
    ghostPosRef.current = { x, y };
    setGhost({
      cardId: press.cardId,
      badge: press.badge,
      width: press.origin.width,
      height: press.origin.height,
      returning: false,
    });
    fnsRef.current.onSelect(press.cardId);
    const slot = slotRefs.current.get(press.cardId);
    try {
      slot?.setPointerCapture(event.pointerId);
    } catch {
      /* capture is optional; window listeners still run */
    }
  };

  useEffect(() => {
    if (phase !== 'drag' || collapsed) return;
    const frame = window.requestAnimationFrame(() => setCollapsed(true));
    return () => window.cancelAnimationFrame(frame);
  }, [collapsed, phase]);

  useEffect(() => {
    if (fns.armed) return;
    if (phaseRef.current === 'drag') startReturn();
    else if (phaseRef.current === 'press') pressRef.current = null;
  }, [fns.armed, startReturn]);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const press = pressRef.current;
      if (!press || event.pointerId !== press.pointerId) return;
      const current = phaseRef.current;
      if (current === 'idle' || current === 'return') return;
      if (current === 'press') {
        const dx = event.clientX - press.startX;
        const dy = event.clientY - press.startY;
        if (dx * dx + dy * dy < REVEAL_DRAG_THRESHOLD * REVEAL_DRAG_THRESHOLD) return;
        if (!fnsRef.current.armed) {
          pressRef.current = null;
          return;
        }
        event.preventDefault();
        beginDrag(event, press);
      }
      if (phaseRef.current !== 'drag') return;
      event.preventDefault();
      moveGhost(event.clientX - press.offsetX, event.clientY - press.offsetY);
      const pile = pileAtPoint(event.clientX, event.clientY);
      const legal = pile != null && fnsRef.current.legalPiles(press.cardId).includes(pile);
      setHover(legal ? pile : null);
    };

    const onUp = (event: PointerEvent) => {
      const press = pressRef.current;
      if (!press || event.pointerId !== press.pointerId) return;
      const current = phaseRef.current;
      if (current !== 'drag') {
        pressRef.current = null;
        if (current === 'press') phaseRef.current = 'idle';
        return;
      }
      const draggedId = press.cardId;
      window.setTimeout(() => {
        if (suppressClickRef.current === draggedId) suppressClickRef.current = null;
      }, 0);
      const pile = pileAtPoint(event.clientX, event.clientY);
      const legal = pile != null && fnsRef.current.legalPiles(press.cardId).includes(pile);
      if (legal && pile != null && fnsRef.current.onDrop(press.cardId, pile)) {
        clearDrag(false);
        return;
      }
      startReturn();
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (phaseRef.current === 'drag') startReturn();
      else if (phaseRef.current === 'press') pressRef.current = null;
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(returnTimerRef.current);
    };
  }, [clearDrag, startReturn]);

  const onPointerDown = useCallback((event: ReactPointerEvent, cardId: string, badge: Badge) => {
    if (event.button !== 0) return;
    if (!fnsRef.current.armed) return;
    if (!fnsRef.current.legalPiles(cardId).length) return;
    const slot = slotRefs.current.get(cardId);
    if (!slot) return;
    const origin = slot.getBoundingClientRect();
    phaseRef.current = 'press';
    pressRef.current = {
      cardId,
      badge,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin,
      offsetX: event.clientX - origin.left,
      offsetY: event.clientY - origin.top,
    };
  }, []);

  const bindSlot = useCallback((cardId: string) => {
    return (node: HTMLDivElement | null) => {
      if (node) slotRefs.current.set(cardId, node);
      else slotRefs.current.delete(cardId);
    };
  }, []);

  const bindGhost = useCallback((node: HTMLDivElement | null) => {
    ghostElRef.current = node;
    if (node) {
      node.style.transform = `translate(${ghostPosRef.current.x}px, ${ghostPosRef.current.y}px)`;
    }
  }, []);

  useLayoutEffect(() => {
    const el = ghostElRef.current;
    if (!el || !ghost) return;
    if (ghost.returning) return;
    el.style.transition = 'none';
    el.style.transform = `translate(${ghostPosRef.current.x}px, ${ghostPosRef.current.y}px)`;
  }, [ghost]);

  const didDrag = useCallback((cardId: string) => {
    if (suppressClickRef.current !== cardId) return false;
    suppressClickRef.current = null;
    return true;
  }, []);

  const slotStyle = (cardId: string): CSSProperties | undefined => {
    if (lifted?.cardId !== cardId) return undefined;
    return { width: collapsed && phase === 'drag' ? 0 : lifted.width };
  };

  const slotClassName = (cardId: string, revealable: boolean) => {
    const liftedHere = lifted?.cardId === cardId;
    return `bb-hand-slot${revealable ? ' is-revealable' : ''}${liftedHere ? ' is-lifted' : ''}${
      liftedHere && collapsed && phase === 'drag' ? ' is-collapsed' : ''
    }`;
  };

  return {
    active: phase === 'drag' || phase === 'return',
    dragging: phase === 'drag',
    ghost,
    bindGhost,
    bindSlot,
    onPointerDown,
    didDrag,
    slotStyle,
    slotClassName,
  };
};
